import { getEnv } from '@/composables/useEnv'
import {
  getTranslation,
  TranslationKey,
  TranslationObject,
  TranslationValue
} from '@/i18n/getTranslation'
import { TPerspective } from '@/services/dataStore/SanityDataStore'
import { useLanguageStore } from '@/store/language'
import { I18nBlockContent, I18nString, PartialI18nString } from '@lifk/types'
import { isStringEmpty } from '@lifk/utils'
import DOMPurify from 'dompurify'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

export const i18nIsNotEmpty = (
  i18n: I18nString | undefined,
  perspective: TPerspective
): i18n is I18nString => {
  // don't validate spanish in draft mode
  const isSpanishEmpty =
    perspective !== 'previewDrafts' && isStringEmpty(i18n?.spanish)
  return !!i18n && !isStringEmpty(i18n.english) && !isSpanishEmpty
}

export const useInlineLanguage = () => {
  const languageStore = useLanguageStore()
  const { language } = storeToRefs(languageStore)

  // Accepts partially translated content; already falls back to English.
  const t = (i18n: PartialI18nString) => {
    const translation = i18n[language.value]
    if (translation && translation.trim().length > 0) {
      return translation
    }
    return i18n.english ?? ''
  }

  const b = (i18n: I18nBlockContent) => {
    const translation = i18n[language.value]
    if (translation?.blocks && translation.blocks.length > 0) {
      return translation.blocks
    }
    return i18n.english?.blocks ?? []
  }
  return { t, b }
}

export default function useLanguage<K extends TranslationKey>(key: K) {
  const languageStore = useLanguageStore()
  const { language } = storeToRefs(languageStore)
  const environment = getEnv('VITE_APP_ENV')

  return computed(
    () =>
      getTranslationsByKey(
        key,
        language.value,
        environment
      ) as TranslationValue<K>
  )
}

const getTranslationsByKey = (
  key: TranslationKey,
  language: 'english' | 'spanish',
  environment: string
): TranslationObject[TranslationKey] => {
  const translations = getTranslation(language)[key] ?? {}

  if (environmentIsNotPublic(environment)) {
    return translations
  } else {
    return modifyTranslations(translations, removeIndicatorAndSanitize)
  }
}

const modifyTranslations = (
  translations,
  modification: (str: string) => string
) => {
  const modifiedTranslations = {}

  if (Array.isArray(translations)) {
    if (translations.length >= 0) {
      if (typeof translations[0] === 'string') {
        return translations.map((str) => removeIndicator(str as string))
      } else if (typeof translations[0] === 'object') {
        return translations.map((obj) =>
          modifyTranslations(obj as object, modification)
        )
      }
    } else {
      return translations
    }
  } else if (typeof translations === 'string') {
    return removeIndicator(translations)
  } else if (typeof translations === 'object') {
    Object.keys(translations).forEach((sectionKey) => {
      modifiedTranslations[sectionKey] = modifyTranslations(
        translations[sectionKey],
        modification
      )
    })
  }

  return modifiedTranslations
}

const removeIndicatorAndSanitize = (str: string): string => {
  const sanitizedStr = sanitize(str)
  return removeIndicator(sanitizedStr)
}

const removeIndicator = (str: string): string => {
  if (str.includes('[###] ')) {
    return str.replace('[###] ', '')
  } else {
    return str
  }
}

const sanitize = (str: string): string => {
  return DOMPurify.sanitize(str, {
    USE_PROFILES: { html: true }
  })
}

const environmentIsNotPublic = (environment: string) => {
  return (
    environment === 'development' ||
    environment === 'review' ||
    environment === 'test'
  )
}
